import { Box, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { normaliseVerificationType } from '../../../constants/verificationType';
import { SharedFileVerification as Verification } from '../../../types/document';
import { formatDateTime } from '../../../utils';
import {
	SHARED_FILE_BLOCKCHAIN_CLASS,
	SHARED_FILE_VERIFICATION_CLASS,
	SHARED_FILE_VERIFIED_CLASS
} from '../../../themes/sharedDocumentsStyles';

const KEYS = 'pages.sharing.recipientView.verification';

/**
 * What a share's recipient is told about one file: that it is not verified, or who
 * verified it, when, and its blockchain record. The server reports only an approved
 * verification as verified, and only names the blockchain when a record was made, so
 * nothing here is claimed that the document does not hold.
 */
const SharedFileVerification = ({ verification }: { verification?: Verification }) => {
	const { t } = useTranslation();

	if (!verification?.verified) {
		return <Typography variant="body2">{t(`${KEYS}.notVerified`)}</Typography>;
	}

	// "Issued", "Authenticated" or "Verified": the same words the holder's wallet uses.
	const type = normaliseVerificationType(verification.verificationType);
	const status = t(`common.types.verification.${type && type !== 'request' ? type : 'verified'}`, {
		defaultValue: t('common.types.verification.verified')
	});

	return (
		<Box className={SHARED_FILE_VERIFICATION_CLASS}>
			<Typography variant="body2" className={SHARED_FILE_VERIFIED_CLASS}>
				{verification.organisationName
					? t(`${KEYS}.byOrganisation`, { status, organisation: verification.organisationName })
					: status}
			</Typography>
			{verification.verifierName && (
				<Typography variant="body2">{t(`${KEYS}.verifier`, { name: verification.verifierName })}</Typography>
			)}
			{verification.verifiedAt && (
				<Typography variant="body2">
					{t(`${KEYS}.verifiedAt`, { date: formatDateTime(verification.verifiedAt) })}
				</Typography>
			)}
			{verification.blockchainId && (
				<Typography variant="body2" className={SHARED_FILE_BLOCKCHAIN_CLASS}>
					{t(`${KEYS}.blockchain`, { id: verification.blockchainId })}
				</Typography>
			)}
		</Box>
	);
};

export default SharedFileVerification;
